import { AuthManager } from "./managers/AuthManager";
import { InteractionManager } from "./managers/InteractionManager";
import { LoadManager } from "./managers/LoadManager";
import { RPCManager } from "./managers/RpcManager";
import { AuthServerMessage, AuthClientMessage } from "./types";

/**
 * A client for interacting with the embedded sandai iframe.
 *
 * The `SandaiClient` handles iframe communication, message passing,
 * and interactions via the `InteractionManager`.
 *
 * ## Usage
 * ```typescript
 * const client = new SandaiClient("myIframeId", "userId", "apiKey");
 * ```
 *
 * @param {string} iframeId - The ID of the target iframe element in the DOM.
 * @throws {Error} Throws an error if the specified element does not exist or is not an iframe.
 */
export class SandaiClient {
  private _iframe: HTMLIFrameElement;
  private _iframeUrl: URL;
  public interactionManager: InteractionManager;
  public loadManager: LoadManager;
  public authManager: AuthManager;
  public rpcManager: RPCManager;

  /**
   * Initializes the `SandaiClient` with a given iframe ID.
   *
   * @param {string} iframeId - The ID of the target iframe element in the DOM.
   * @throws {Error} Throws an error if the specified element does not exist or is not an iframe.
   */
  constructor(
    iframeId: string,
    userId?: AuthClientMessage["userId"],
    apiKey?: AuthClientMessage["apiKey"],
    debugOptions?: {
      skipAuth?: boolean;
      skipLoadCheck?: boolean;
    },
  ) {
    const iframe = document.getElementById(iframeId);
    if (!iframe) {
      throw new Error(`Element with ID "${iframeId}" not found.`);
    }

    if (!(iframe instanceof HTMLIFrameElement)) {
      throw new Error(
        `Element with ID "${iframeId}" is not an <iframe>. Tag was ${iframe.tagName}`,
      );
    }

    this._iframe = iframe as HTMLIFrameElement;
    this._iframeUrl = new URL(iframe.src);
    this.interactionManager = new InteractionManager(this._iframe);
    this.rpcManager = new RPCManager(this._iframe);
    this.loadManager = new LoadManager(
      this._iframe,
      debugOptions?.skipLoadCheck,
    );
    this.authManager = new AuthManager(
      this._iframe,
      userId,
      apiKey,
      debugOptions?.skipAuth,
    );
  }

  /**
   * Initializes both the LoadManager and AuthManager instances in sequence.
   *
   * This ensures that:
   * 1. The iframe is fully loaded and in a "ready" state.
   * 2. Authentication is performed and the user's access tier is retrieved.
   *
   * @async
   * @returns {Promise<void>} A promise that resolves once both managers have completed initialization.
   */
  async init(): Promise<{
    loadState: Awaited<ReturnType<SandaiClient["_initLoadManager"]>>;
    authState: Awaited<ReturnType<SandaiClient["_initAuthManager"]>>;
  }> {
    this.destroy();
    console.log("Initializing Sandai Client...");
    const loadState = await this._initLoadManager();
    const authState = await this._initAuthManager();
    console.log("Initialized Sandai Client!");
    return {
      loadState,
      authState,
    };
  }

  private async _initLoadManager(): ReturnType<LoadManager["init"]> {
    const initState = await this.loadManager.init();
    if (initState.ready) {
      return initState;
    }
    if (initState.state === "loadError") {
      console.warn(
        "[sandai-core] Trying to resolve the issue by fetching the model from outside the iframe. " +
          "If the error is a CORS issue, then this might resolve it. It shouldn't be a problem " +
          "once the model is hosted on a ssl-secured non-local domain.",
      );
      const res = await fetch(
        new URL(this._iframeUrl).searchParams.get("vrmUrl")!,
      );
      const vrmAB = await res.arrayBuffer();
      const vrmBlobUrl = await this.rpcManager.postLoadErrorCall(
        "arrayBufferProvider.urlify",
        vrmAB,
      );
      await this.rpcManager.postLoadErrorCall("updateVrmUrl", vrmBlobUrl);
      return await this._initLoadManager();
    }
    if (initState.state === "timeout") {
      console.error(
        "[sandai-core] Load Manager timed out. Proceeding without knowing the Load State",
      );
    }
    return initState;
  }

  private async _initAuthManager() {
    return await this.authManager.init();
  }

  /**
   * Sends a message to the iframe.
   *
   * @private
   * @param {*} data - The data payload to be sent to the iframe.
   * @throws {Error} Throws an error if the iframe does not have a `contentWindow`.
   */
  _sendMessage(data: any) {
    if (this._iframe.contentWindow) {
      this._iframe.contentWindow.postMessage(data, "*");
    } else {
      throw new Error("iframe doesn't have a 'contentWindow' member");
    }
  }

  /**
   * Stores all registered message event listeners for later removal.
   *
   * @private
   */
  private _listeners: Array<(event: MessageEvent<any>) => void> = [];

  /**
   * Listens for messages from the iframe and invokes the provided callback when a message is received.
   * Only messages from the iframe's origin are processed.
   *
   * The listener is stored internally so it can be removed later via `destroy()`.
   *
   * @private
   * @param {(data: any) => void} callback - The callback function to invoke with the message data.
   */
  private _listenForMessage(callback: (data: any) => void) {
    const listener = (event: MessageEvent<any>) => {
      if (event.origin !== this._iframeUrl.origin) return;
      callback(event.data);
    };
    this._listeners.push(listener);
    window.addEventListener("message", listener);
  }

  /**
   * Cleans up all registered message event listeners previously added by `_listenForMessage`.
   * Call this method when the instance is being destroyed or no longer needs to listen to messages.
   */
  destroy() {
    console.log("Destroying Sandai Client...");
    this.loadManager.destroy();
    this.authManager.destroy();
    this._listeners.forEach((listener) => {
      window.removeEventListener("message", listener);
    });
    this._listeners = [];
    console.log("Destroyed Sandai Client");
  }

  /**
   * Retrieves documentation for the interaction manager.
   *
   * @private
   * @returns {Map<string, Map<string, Map<string, { schema: any[]; func: Function; }> | Map<string, { schema: any[]; func: Function; }>>>}
   *          A `Map` containing nested documentation structures for available managers.
   */
  _getDocs() {
    const interactionManagerDocs = this.interactionManager._getDocs();

    const docs = new Map<string, typeof interactionManagerDocs>();
    docs.set("interactionManager", interactionManagerDocs);

    return docs;
  }
}
